import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UIWheelSegments } from "../../common_ui_elements/ui_wheel_segments.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import type { UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { ConveyorRecord } from "../../../conveyor/conveyor_record.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { hapticsController, VibrationContext } from "../../../haptics/haptics_controller.js";
import { HapticPattern } from "../../../haptics/haptics_constants.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { RewardFlySources } from "../../reward_fly/reward_fly_sources.js";

export interface ConveyorResultOverlayParams
{
  won: boolean;
  level: number;
  seconds: number;
  time_limit: number;
  rewards: DailyTaskRewardDto[];
  stats_before: GameStatistics;
  onExit: () => void;
  onContinue: () => void;
}

interface Box { x: number; y: number; w: number; h: number; }

interface ResultLayout
{
  logo_y: number;
  title_y: number;
  button_y: number;
}

const DESIGN_W = 1080;
const DESIGN_H = 1920;

// One overlay for both ends of a conveyor level: a passed level adds the
// ribbon, the close button and the reward block; a lost one only swaps the
// texts and the buttons.
export class ConveyorResultOverlay extends AbstractOverlay
{
  private static readonly TITLE_POP_MS = 160;
  private static readonly TITLE_POP_SCALE = 0.6;
  private static readonly TITLE_HOLD_MS = 300;
  private static readonly TITLE_RISE_MS = 210;
  private static readonly BG_FADE_MS = 140;
  private static readonly BANNER_SLIDE_MS = 190;
  private static readonly LOGO_APPEAR_MS = 140;
  private static readonly LOGO_APPEAR_SCALE = 0.6;
  private static readonly CONTENT_DELAY_MS = 30;
  private static readonly CONTENT_APPEAR_MS = 130;

  private static readonly WIN_LAYOUT: ResultLayout = {
    logo_y: 135,
    title_y: 465,
    button_y: 1500,
  };

  private static readonly LOSE_LAYOUT: ResultLayout = {
    logo_y: 290,
    title_y: 625,
    button_y: 1530,
  };

  private static readonly LOGO_W = 560;
  private static readonly LOGO_H = 315;

  private static readonly BANNER_HALF_W = 517;
  private static readonly BANNER_H = 275;
  private static readonly BANNER_Y = 195;
  private static readonly BANNER_SEAM_OVERLAP = 4;

  private static readonly CLOSE_SIZE = 130;
  private static readonly CLOSE_X = 880;
  private static readonly CLOSE_Y = 160;

  private static readonly TITLE_W = 960;
  private static readonly TITLE_H = 117;
  private static readonly TITLE_FONT = 76;
  private static readonly TITLE_MIN_FONT = 44;

  private static readonly LABEL_W = 700;
  private static readonly LABEL_H = 70;
  private static readonly LABEL_FONT = 50;

  private static readonly REWARD_LABEL_Y = 665;
  private static readonly REWARD_Y = 720;
  private static readonly REWARD_SIZE = 200;
  private static readonly REWARD_GAP = 40;
  private static readonly REWARD_AMOUNT_Y = 875;
  private static readonly REWARD_AMOUNT_FONT = 52;
  private static readonly REWARD_GLOW_RADIUS = 115;
  private static readonly REWARD_GLOW_ROTATION_SPEED = 0.45;

  private static readonly TIME_LABEL_Y = 880;
  private static readonly TIME_VALUE_Y = 975;
  private static readonly TIME_VALUE_H = 110;
  private static readonly TIME_VALUE_FONT = 92;

  private static readonly CONTINUE_BUTTON_W = 740;
  private static readonly BUTTON_W = 554;
  private static readonly BUTTON_H = 240;
  private static readonly RETURN_BUTTON_X = 2;
  private static readonly PLAY_AGAIN_BUTTON_X = 520;
  private static readonly BUTTON_FONT = 60;

  private static readonly GOLD_GRADIENT = {
    direction: "vertical" as const,
    stops: [
      { offset: 0, color: ColorScheme.conveyor_result_value_top },
      { offset: 1, color: ColorScheme.conveyor_result_value_bottom },
    ],
  };

  private readonly appear_start = performance.now();
  private readonly content: UIElement[] = [];
  private readonly banner_halves: UIImage[] = [];
  private readonly reward_glows: IController[] = [];
  private readonly reward_icons: { reward: DailyTaskRewardDto; icon: UIImage }[] = [];
  private logo!: UIImage;
  private title!: UITextElement;
  private interactive_at = 0;
  private closed = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: ConveyorResultOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    if ( params.won )
      this.createBanner();

    this.createLogo();
    this.createTitle();

    if ( params.won )
    {
      this.createRewards();
      this.createCloseButton();
    }
    else
    {
      this.createTime();
    }

    this.createButtons();
    this.startAppearAnimation();

    this.interactive_at = this.contentAt() + ConveyorResultOverlay.CONTENT_APPEAR_MS * 0.6;
  }

  private get result(): ConveyorResultOverlayParams
  {
    return this.params as ConveyorResultOverlayParams;
  }

  private get layout(): ResultLayout
  {
    return this.result.won ? ConveyorResultOverlay.WIN_LAYOUT : ConveyorResultOverlay.LOSE_LAYOUT;
  }

  private riseAt(): number
  {
    return this.appear_start + ConveyorResultOverlay.TITLE_POP_MS + ConveyorResultOverlay.TITLE_HOLD_MS;
  }

  private backgroundAt(): number
  {
    return this.riseAt() + ConveyorResultOverlay.TITLE_RISE_MS;
  }

  private bannerAt(): number
  {
    return this.backgroundAt() + ConveyorResultOverlay.BG_FADE_MS;
  }

  private logoAt(): number
  {
    return this.bannerAt() + ( this.result.won ? ConveyorResultOverlay.BANNER_SLIDE_MS : 0 );
  }

  private contentAt(): number
  {
    return this.logoAt() + ConveyorResultOverlay.LOGO_APPEAR_MS + ConveyorResultOverlay.CONTENT_DELAY_MS;
  }

  private addContent( element: UIElement ): void
  {
    this.addChild( element );
    this.content.push( element );
  }

  private box( dx: number, dy: number, dw: number, dh: number ): Box
  {
    const w = LayoutScaler.widthFromDesign( dw );
    const h = LayoutScaler.heightFromDesign( dh );
    const cx = this.rect.getW2() + LayoutScaler.widthFromDesign( dx + dw / 2 - DESIGN_W / 2 );
    const cy = this.rect.getH2() + LayoutScaler.heightFromDesign( dy + dh / 2 - DESIGN_H / 2 );

    return { x: cx - w / 2, y: cy - h / 2, w, h };
  }

  private centered( dy: number, dw: number, dh: number ): Box
  {
    const dx = ( DESIGN_W - dw ) / 2;

    return this.box( dx, dy, dw, dh );
  }

  private shadow(): Partial<UITextOptions>
  {
    return {
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.conveyor_result_shadow,
      shadow_offset_x: 0,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
    };
  }

  private createBanner(): void
  {
    const banner_w = ConveyorResultOverlay.BANNER_HALF_W * 2;
    const b = this.centered( ConveyorResultOverlay.BANNER_Y, banner_w, ConveyorResultOverlay.BANNER_H );
    const half_w = b.w / 2;
    const overlap = LayoutScaler.widthFromDesign( ConveyorResultOverlay.BANNER_SEAM_OVERLAP );
    const right_x = b.x + half_w - overlap;

    const left = new UIImage( this.ctx, b.x, b.y, half_w, b.h, ImgID.UI_ConveyorResultBanner );
    left.setClickable( false );
    this.addChild( left );

    const right = new UIImage( this.ctx, right_x, b.y, half_w, b.h, ImgID.UI_ConveyorResultBanner );
    right.setFlipX( true );
    right.setClickable( false );
    this.addChild( right );

    this.banner_halves.push( left, right );
  }

  private createLogo(): void
  {
    const b = this.centered( this.layout.logo_y, ConveyorResultOverlay.LOGO_W, ConveyorResultOverlay.LOGO_H );

    this.logo = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_ConveyorLoadingLogo );
    this.logo.setClickable( false );
    this.addChild( this.logo );
  }

  private titleText(): string
  {
    const text = this.result.won
      ? LocalizationManager.tWithParams( "CONVEYOR.LEVEL_PASSED", [ String( this.result.level ) ] )
      : LocalizationManager.t( "CONVEYOR.GAME_OVER" );

    return text.toLocaleUpperCase();
  }

  private createTitle(): void
  {
    const b = this.centered( this.layout.title_y, ConveyorResultOverlay.TITLE_W, ConveyorResultOverlay.TITLE_H );

    this.title = new UITextElement( this.ctx, b.x, b.y, b.w, b.h, {
      text: this.titleText(),
      font_size: LayoutScaler.textSizeFromDesign( ConveyorResultOverlay.TITLE_FONT ),
      font_weight: 900,
      gradient_text_color: ConveyorResultOverlay.GOLD_GRADIENT,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
      outline_color: ColorScheme.conveyor_result_outline,
      auto_wrap: false,
      letter_spacing: LayoutScaler.widthFromDesign( 2 ),
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( ConveyorResultOverlay.TITLE_MIN_FONT ),
      ...this.shadow(),
    } );
    this.addChild( this.title );
  }

  private createCloseButton(): void
  {
    const b = this.box( ConveyorResultOverlay.CLOSE_X, ConveyorResultOverlay.CLOSE_Y, ConveyorResultOverlay.CLOSE_SIZE, ConveyorResultOverlay.CLOSE_SIZE );

    const button = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_SettingsDialogCloseBtn, undefined, true );
    button.setOnClick( () => this.finish( this.result.onExit ) );
    this.addContent( button );
  }

  private addLabel( key: string, dy: number ): void
  {
    const b = this.centered( dy, ConveyorResultOverlay.LABEL_W, ConveyorResultOverlay.LABEL_H );

    this.addContent( new UITextElement( this.ctx, b.x, b.y, b.w, b.h, {
      localization_key: key,
      font_size: LayoutScaler.textSizeFromDesign( ConveyorResultOverlay.LABEL_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      outline_color: ColorScheme.booster_dialog_quantity_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } ) );
  }

  private addValue( text: string, dy: number, dh: number, font: number ): void
  {
    const b = this.centered( dy, ConveyorResultOverlay.LABEL_W, dh );

    this.addContent( new UITextElement( this.ctx, b.x, b.y, b.w, b.h, {
      text,
      font_size: LayoutScaler.textSizeFromDesign( font ),
      font_weight: 900,
      gradient_text_color: ConveyorResultOverlay.GOLD_GRADIENT,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
      outline_color: ColorScheme.conveyor_result_outline,
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      auto_wrap: false,
      shadow_offset_x: 0,
      shadow_offset_y: 0
    } ) );
  }

  private createRewards(): void
  {
    const rewards = this.result.rewards;
    if ( rewards.length === 0 )
      return;

    this.addLabel( "CONVEYOR.REWARD_LABEL", ConveyorResultOverlay.REWARD_LABEL_Y );

    const size = ConveyorResultOverlay.REWARD_SIZE;
    const gap = ConveyorResultOverlay.REWARD_GAP;
    const row_w = rewards.length * size + ( rewards.length - 1 ) * gap;
    const row_x = ( DESIGN_W - row_w ) / 2;

    rewards.forEach( ( reward, i ) => this.createReward( reward, row_x + i * ( size + gap ) ) );
  }

  private createReward( reward: DailyTaskRewardDto, dx: number ): void
  {
    const size = ConveyorResultOverlay.REWARD_SIZE;
    const icon_box = this.box( dx, ConveyorResultOverlay.REWARD_Y, size, size );
    const overlay_absolute = this.getAbsolutePosition();
    const glow_x = overlay_absolute.x + icon_box.x + icon_box.w / 2;
    const glow_y = overlay_absolute.y + icon_box.y + icon_box.h / 2;
    const glow_radius = LayoutScaler.widthFromDesign( ConveyorResultOverlay.REWARD_GLOW_RADIUS );

    this.reward_glows.push( Vfx2DManager.addCoinCounterGlow(
      new Vector2( glow_x, glow_y ),
      glow_radius,
      this.contentAt(),
      0,
      true,
      ConveyorResultOverlay.CONTENT_APPEAR_MS,
      true,
      ConveyorResultOverlay.REWARD_GLOW_ROTATION_SPEED
    ) );

    const icon_id = UIWheelSegments.rewardIcon( reward );
    if ( icon_id !== null )
    {
      const icon = new UIImage( this.ctx, icon_box.x, icon_box.y, icon_box.w, icon_box.h, icon_id );
      icon.setClickable( false );
      this.addContent( icon );
      this.reward_icons.push( { reward, icon } );
    }

    const amount_dx = dx + size / 2 - ConveyorResultOverlay.LABEL_W / 2;
    const amount = this.box( amount_dx, ConveyorResultOverlay.REWARD_AMOUNT_Y, ConveyorResultOverlay.LABEL_W, ConveyorResultOverlay.LABEL_H );

    this.addContent( new UITextElement( this.ctx, amount.x, amount.y, amount.w, amount.h, {
      text: UIWheelSegments.rewardLabel( reward ),
      font_size: LayoutScaler.textSizeFromDesign( ConveyorResultOverlay.REWARD_AMOUNT_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.friends_title_outline,
      auto_wrap: false,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
    } ) );
  }

  private createTime(): void
  {
    const text = `${ConveyorRecord.format( this.result.seconds )}/${ConveyorRecord.format( this.result.time_limit )}`;

    this.addLabel( "CONVEYOR.TIME_LABEL", ConveyorResultOverlay.TIME_LABEL_Y );
    this.addValue( text, ConveyorResultOverlay.TIME_VALUE_Y, ConveyorResultOverlay.TIME_VALUE_H, ConveyorResultOverlay.TIME_VALUE_FONT );
  }

  private createButtons(): void
  {
    const y = this.layout.button_y;

    if ( this.result.won )
    {
      const x = ( DESIGN_W - ConveyorResultOverlay.CONTINUE_BUTTON_W ) / 2;

      this.createButton( x, y, ConveyorResultOverlay.CONTINUE_BUTTON_W, ImgID.UI_GreenFrameBtn, "green_frame_btn", "CONVEYOR.CONTINUE", ColorScheme.menu_button_outline, this.result.onContinue );
      return;
    }

    this.createButton(
      ConveyorResultOverlay.RETURN_BUTTON_X,
      y,
      ConveyorResultOverlay.BUTTON_W,
      ImgID.UI_BlueBtn,
      "blue_btn",
      "CONVEYOR.EXIT",
      ColorScheme.conveyor_result_return_outline,
      this.result.onExit
    );
    this.createButton(
      ConveyorResultOverlay.PLAY_AGAIN_BUTTON_X,
      y,
      ConveyorResultOverlay.BUTTON_W,
      ImgID.UI_GreenFrameBtn,
      "green_frame_btn",
      "CONVEYOR.PLAY_AGAIN",
      ColorScheme.menu_button_outline,
      this.result.onContinue
    );
  }

  private createButton( dx: number, dy: number, dw: number, image: ImgID, ns_key: string, key: string, outline_color: string, action: () => void ): void
  {
    const b = this.box( dx, dy, dw, ConveyorResultOverlay.BUTTON_H );

    const button = new UINineSliceElement( this.ctx, b.x, b.y, b.w, b.h, image, ns_key, undefined, undefined, true );

    button.addChild( new UITextElement( this.ctx, 0, 0, b.w, b.h, {
      localization_key: key,
      font_size: LayoutScaler.textSizeFromDesign( ConveyorResultOverlay.BUTTON_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      using_y_offset: false,
      outline_color,
      ...this.shadow(),
    } ), true );

    button.setOnClick( () => this.finish( action ) );
    this.addContent( button );
  }

  private finish( action: () => void ): void
  {
    if ( this.closed )
      return;

    this.closed = true;
    void hapticsController.vibrate( HapticPattern.LIGHT, VibrationContext.GENERAL );

    const sources = this.reward_icons.map( ( { reward, icon } ) => RewardFlySources.fromElement( reward, icon ) );
    RewardFlyToHud.playThenClose( this.ctx, this, sources, this.result.stats_before, () => this.close( action ) );
  }

  private close( action: () => void ): void
  {
    try
    {
      action();
    }
    catch ( error )
    {
      console.error( "[ConveyorResultOverlay] Close failed:", error );
    }
    finally
    {
      OverlayManager.closeOverlay();
    }
  }

  private startAppearAnimation(): void
  {
    this.animateTitle();
    OverlayManager.fadeInBackground( this.backgroundAt() - performance.now(), ConveyorResultOverlay.BG_FADE_MS );
    this.animateBanner();
    this.animateLogo();
    this.animateContent();
  }

  private animateTitle(): void
  {
    const title = this.title;
    const final_pos = title.pixelPosImportant().clone();
    const title_h = title.getRect().getH();
    const start_y = this.rect.getH2() - title_h / 2;
    const start_pos = new Vector2( final_pos.x, start_y );
    const pop_scale = new Vector2( ConveyorResultOverlay.TITLE_POP_SCALE, ConveyorResultOverlay.TITLE_POP_SCALE );

    title.setPixelPos( start_pos, true );
    title.setScale( pop_scale );
    title.setOpacity( 0 );

    title.addTransform( new TransformScale( title, true, pop_scale, new Vector2( 1, 1 ), this.appear_start, ConveyorResultOverlay.TITLE_POP_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformOpacity( title, true, 0, 1, this.appear_start, ConveyorResultOverlay.TITLE_POP_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformStraightMove( title, true, start_pos, final_pos, this.riseAt(), ConveyorResultOverlay.TITLE_RISE_MS, { easing: Easing.EaseInOutQuad } ) );
  }

  private animateBanner(): void
  {
    const shift = this.rect.getW();

    this.banner_halves.forEach( ( half, i ) =>
    {
      const final_pos = half.pixelPosImportant().clone();
      const side = i === 0 ? -1 : 1;
      const start_x = final_pos.x + side * shift;
      const start_pos = new Vector2( start_x, final_pos.y );

      half.setPixelPos( start_pos, true );
      half.addTransform( new TransformStraightMove( half, true, start_pos, final_pos, this.bannerAt(), ConveyorResultOverlay.BANNER_SLIDE_MS, { easing: Easing.EaseOutQuad } ) );
    } );
  }

  private animateLogo(): void
  {
    const logo = this.logo;
    const start_scale = new Vector2( ConveyorResultOverlay.LOGO_APPEAR_SCALE, ConveyorResultOverlay.LOGO_APPEAR_SCALE );

    logo.setScale( start_scale );
    logo.setOpacity( 0 );

    logo.addTransform( new TransformScale( logo, true, start_scale, new Vector2( 1, 1 ), this.logoAt(), ConveyorResultOverlay.LOGO_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    logo.addTransform( new TransformOpacity( logo, true, 0, 1, this.logoAt(), ConveyorResultOverlay.LOGO_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
  }

  private animateContent(): void
  {
    for ( const element of this.content )
    {
      element.setOpacity( 0 );
      element.addTransform( new TransformOpacity( element, true, 0, 1, this.contentAt(), ConveyorResultOverlay.CONTENT_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    }
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( now < this.interactive_at || this.closed )
      return true;

    return super.onTouchStart( p, now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public override onOverlayWillClose(): void
  {
    for ( const glow of this.reward_glows )
      glow.stop();

    this.reward_glows.length = 0;
  }

  public name(): string
  {
    return "ConveyorResultOverlay";
  }
}
